<template>
  <layout title="Privacy Policy">
    <textFirstSection title="Privacy Policy" />
    <div class="flex justify-center p-6">
      <div class="mt-6 html-editor max-w-6xl mb-6">
        <h5>LAST UPDATED: 21 SEPTEMBER 2026</h5>

        <p>
          <strong
            >Youverse simplifies all consumer interactions with secure and
            private identity and face verification on any device in industries
            as diverse as Retail, Banking &amp; Payments, Travel &amp;
            Hospitality, Healthcare, Self-Service &amp; Delivery. Register once
            and access services anywhere, anytime by just using your face. The
            information we collect and use helps us with that mission—and that’s
            it</strong
          >. At Youverse, when we perform identity and face verification (our
          “<strong>Identity and Face Verification Services</strong>“), we’re
          committed to protecting the privacy of that person. This Privacy
          Policy is meant to help you understand how we use the information we
          collect to provide our Identity and Face Verification Services on
          behalf of our clients and build trust in our system. When we provide
          Identity and Face Verification Services to our clients, we are acting
          on their behalf as their service provider and processor. Our clients,
          and not us, are the controllers of the data you submit when
          interacting with our Identity and Face Verification Services. For
          additional information about how your specific data is being collected
          and used, please review the privacy policy of our client who is using
          our Identity and Face Verification Services with you.<br />
        </p>

        <p>
          For details about the information we collect on our websites and
          online platforms, including how we use cookies, please visit our
          <inertia-link :href="$route('cookiespolicy')" class="link"
            >Cookie Policy</inertia-link
          >.<br />We may need to update this Privacy Policy from time to time,
          so we recommend you check back periodically. If we make any
          substantial changes, we may notify you via email or by posting a
          notice on our website.
        </p>

        <h2>Definitions</h2>

        <p>For the purposes of this Privacy Policy:</p>

        <ul>
          <li>
            <strong>Company</strong> (referred in this Agreement as either
            “<strong>we</strong>“, “<strong>us</strong>“, “<strong>our</strong>”
            or “<strong>Youverse</strong>”) refers to YOONIK – AUTHENTICATION
            AND PRIVACY SYSTEMS, S.A..
          </li>
          <li>
            <strong>Services</strong> refer to the websites, mobile
            applications, software development kits and other products and
            services that have linked to these Agreement offered by YOONIK –
            AUTHENTICATION AND PRIVACY SYSTEMS, S.A..
          </li>
          <li>
            <strong>You</strong> mean the individual accessing or using the
            Services, or the company, or other legal entity on behalf of which
            such individual is accessing or using the Services, as applicable.
          </li>
        </ul>

        <h2>
          The Information We Collect and the Youverse Identity and Face
          Verification Lifecycle
        </h2>

        <p>
          To provide our Identity and Face Verification Services, we need to
          collect certain information about our clients’ users or directly from
          our users. The exact information that is needed depends on the check
          that’s being carried out: (1) for online authentication, our services
          will need to receive an id/username as well as a picture or video of
          their face. We’ll then seek to verify whether the person in the photo
          or video is likely the same person that is registered with the
          provided id/username; (2) for verification of ID document/Passport,
          our services will need to receive a live photo of the user as well as
          the user photo in their ID document/Passport. We will then seek to
          verify whether the person in the live photo is likely the same person
          in the photo of the ID document/Passport.<br />
        </p>

        <p>
          In order to do the case (1) explained above, at registration time, we
          store the id/username, and we generate and store an encrypted version
          of the biometric template from the provided photo. This biometric
          template is created using proprietary deep learning models and cannot
          be reversed to the original image.
          <strong
            >After creating the biometric template, the user photo is
            deleted</strong
          >. After the registration process/ first time use, we generate a
          biometric template for each new query photo and compare it against the
          registration template to check if it is likely to be a match.
          <strong>We do not store any user photos</strong>. We may store
          additional user information like name and e-mail when the user
          explicitly submits this information.<br />
        </p>

        <p>
          The Youverse Identity and Face Verification Lifecycle below shows the
          different sources of the information that we collect.
        </p>

        <ol>
          <li>
            <strong>The Client</strong><br />Clients are organizations that have
            asked Youverse to perform identity and face verification for a user.
            Once we have run our checks, we share the results with the client.
            The client then decides how they want to proceed with the user based
            on the results.
          </li>
          <li>
            <strong>The User</strong><br />Users are individuals whose accounts
            we authenticate using facial biometrics on behalf of our clients or
            directly with us. We collect users’ information from clients or
            directly from the users themselves. This information might include
            the biometric facial identifiers extracted by Youverse (biometric
            templates) from photos or a video provided by the user and/or their
            username/id. This enables us to help the client authenticate the
            user by using their face.
          </li>
          <li>
            <strong>Usage logs</strong><br />We also keep logs of how our
            clients and users interact with our Services. This might include
            timestamps of when the information was submitted to Youverse, and
            information about the device used to submit that information.
          </li>
        </ol>

        <p>
          Sometimes, we receive information we don’t need to provide our
          Services. For example, instead of a picture of their face, a user
          might upload a completely unrelated image. When this happens, we seek
          to delete this data.
        </p>

        <h2>
          Using Information for our Identity and Face Verification Services
        </h2>

        <p>
          We use information to provide and maintain our Identity and Face
          Verification Services on behalf of clients on the basis that the user
          has consented to the processing or otherwise requested Identity and
          Face Verification Services, the client has a legitimate or lawful
          reason for requesting Identity and Face Verification Services, or the
          processing is necessary to carry out a task in the public interest or
          for reasons of substantial public interest.
        </p>

        <p>
          We also use information to further develop our Identity and Face
          Verification Services on the basis that the processing is necessary in
          the legitimate interest of the client or Youverse, the processing is
          necessary to carry out a task in the public interest or for reasons of
          substantial public interest, the processing is necessary for
          scientific research purposes, or the user has provided their consent.
        </p>

        <h2>Sharing Information Outside Youverse</h2>

        <p>
          As well as sharing information with clients and users (as described
          above), Youverse also shares information with with other
          organizations, government bodies, and individuals outside Youverse
          where we have a legitimate legal reason for doing so (for example, in
          connection with any merger or acquisition or to comply with a court
          order) or where we have been instructed to share the information on
          behalf of our clients.<br />
        </p>

        <p>
          Whenever legally possible, we seek to protect the information we share
          by imposing contractual privacy and security safeguards on the
          recipient of the information. This is particularly important in cases
          where the recipient is located in a country that has different or
          lesser privacy laws than those of the country where the information
          was originally collected. In some cases, however, it’s not possible
          for us to do so — for example, when we have a legal obligation to
          disclose information to a government authority and that government
          authority isn’t willing to enter into such contractual safeguards.
        </p>

        <h2>Information Security</h2>

        <p>
          Youverse takes appropriate administrative, physical, technical and
          organizational measures designed to help protect information about
          users from loss, theft, misuse and unauthorized access, disclosure,
          alteration and destruction. If you think you have identified a
          security vulnerability or bug in our Identity and Face Verification
          Services, please report it to the Youverse support team at
          <a href="mailto:support@youverse.id">support@youverse.id</a>.
        </p>

        <h2>Data Storage</h2>

        <p>
          We perform our Identity and Face Verification Services on behalf of
          our clients for a variety of different reasons. Those reasons are
          identified by our clients, and we rely on them to tell us when they no
          longer need us to store the information we’ve collected on their
          behalf. Once instructed, either through our agreement with the client
          or through an ad hoc request, we delete the information we have
          collected about users when performing the requested Identity and Face
          Verification Services.<br />
        </p>

        <p>
          If you, as a user, would like to make a specific request to have your
          information deleted, please make that request directly to the client
          that carried out your related check. In case you have registered
          directly with Youverse, please make the request to us. For more
          information about how to do this, please see below under “Your
          Rights”.<br />
        </p>

        <p>
          Where we have a legitimate legal reason, we may also store information
          for longer than described above – for example, where we are under a
          binding legal order not to destroy information.
        </p>

        <h2>
          Information We Collect as Controller: Website, Marketing and Events
        </h2>

        <p>
          The sections above describe how we process personal data as a
          processor when providing Identity and Face Verification Services on
          behalf of our clients. This section describes the separate, more
          limited circumstances in which Youverse acts as the
          <strong>data controller</strong> — namely when you visit our website,
          contact us, subscribe to our communications, or register for an event
          or webinar we host.
        </p>

        <h4>What we collect</h4>

        <p>
          When you register for one of our webinars,
          events, or demos, or when you fill in a contact, download, or
          subscription form on our website, we collect the information you
          provide directly. This typically includes your name, email address,
          company or organisation, job title, and any other details you choose
          to submit, together with information about your registration and
          attendance (for example, whether you attended and any questions you
          submit). If you attend a webinar hosted on a third-party platform,
          that platform may also collect technical information such as your IP
          address and device details.
        </p>

        <h4>How we use it</h4>

        <p>
          We use this information to: register you for
          and deliver the event or webinar; send you related communications,
          including reminders, follow-ups, and materials; respond to your
          enquiries; and, where you have agreed or where we have a legitimate
          interest in doing so, send you marketing communications about our
          products and services. You can withdraw your consent or opt out of
          marketing communications at any time using the unsubscribe link in our
          emails or by contacting us at the address below.
        </p>

        <h4>Legal basis</h4>

        <p>
          We rely on your <strong>consent</strong> where
          you have opted in to receive marketing communications, and on our
          <strong>legitimate interests</strong> in promoting our business,
          organising events, and understanding how our website and
          communications are used, balanced against your rights and interests.
          Where you register for an event, we also process your data as
          necessary to <strong>perform our agreement</strong> to provide you
          with that event.
        </p>

        <h4>Sharing</h4>

        <p>
          We share this information with service providers who
          help us run our website, events, and communications on our behalf.
          These include Microsoft Teams (for hosting webinars and video calls),
          Mailchimp and HubSpot (for email communications and customer
          relationship management), and Google Analytics (for understanding how
          our website is used). These providers act on our instructions under
          appropriate data protection terms. Some of these providers may process
          data outside the European Economic Area; where they do, we put in
          place appropriate safeguards such as the European Commission’s Standard
          Contractual Clauses.
        </p>

        <h4>Retention</h4>

        <p>
          We keep event and marketing data for as long as
          necessary for the purposes described above — for up to 24 months after
          an event, and for marketing purposes until you unsubscribe or we have
          not engaged with you for 6 months. We then delete or anonymise it.
        </p>

        <h4>Your rights</h4>

        <p>
          You have the rights described in the “Your
          Rights” section below, including the right to access, correct, or
          delete your personal data, to object to or restrict processing, to
          data portability, and to withdraw consent. To exercise any of these
          rights in relation to data we hold as controller, contact us at
          <a href="mailto:support@youverse.id">support@youverse.id</a>.
        </p>

        <h4>Cookies</h4>

        <p>
          For information about how we use cookies and similar
          technologies on our website, please see our
          <inertia-link :href="$route('cookiespolicy')" class="link"
            >Cookie Policy</inertia-link
          >.
        </p>

        <h2>Your Rights</h2>

        <p>
          If you would like to access a copy of, delete, or otherwise exercise
          control over your personal information, please contact Youverse at
          <a href="mailto:support@youverse.id">support@youverse.id</a>. Please
          be aware, for most requests Youverse may need to notify the relevant
          client (as described above in the Youverse Identity and Face
          Verification Lifecycle) so the client (and not Youverse) may fulfill
          the request. This is necessary where Youverse is acting on the
          client’s behalf.
        </p>

        <h2>Contact Youverse or a Privacy Supervisory Authority</h2>

        <p>
          If you would like more information about how Youverse collects and
          uses information, please contact Youverse at
          <a href="mailto:support@youverse.id">support@youverse.id</a>.<br />
        </p>

        <p>
          If you’d like to raise a concern with a Privacy Supervisory Authority
          in a member state of the European Union, a list of contact points is
          available
          <a
            href="https://digital-strategy.ec.europa.eu/en/library/list-personal-data-protection-competent-authorities"
            data-type="URL"
            target="_blank"
            data-id="https://digital-strategy.ec.europa.eu/en/library/list-personal-data-protection-competent-authorities"
            >here</a
          >.
        </p>
      </div>
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../Components/Layout/layout.vue");
const textFirstSection = () =>
  import(
    /* webpackChunkName: "textFirstSection" */ "../Components/Layout/textFirstSection.vue"
  );
export default {
  components: { layout, textFirstSection },
};
</script>

<style>
@import "../../css/custom.css";
</style>